import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { getTenant } from '../lib/tenant';
import { InspectionDashboard } from '../components/dashboard/InspectionDashboard';
import { BrainPanel } from '../components/brain/BrainPanel';
import { QuoteCart } from '../components/shop/QuoteCart';

const BRAIN_ENABLED = import.meta.env.VITE_BRAIN_ENABLED !== 'false';

export function Dashboard() {
  const { profile, signOut } = useAuth();
  const { tenant: activeTenant, membership } = useTenant();
  const tenant = getTenant();
  const [brainOpen, setBrainOpen] = useState(false);

  const firstName = profile?.display_name?.split(' ')[0]
    ?? profile?.full_name?.split(' ')[0]
    ?? '';

  const canSeeBrain = BRAIN_ENABLED && (membership?.role === 'owner' || membership?.role === 'admin');

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10 px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-7 w-7 rounded-md bg-brand-600" />
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-brand-400">Velocity 9 Admin</div>
              <div className="text-sm font-semibold">{activeTenant?.name ?? tenant.tenant.name}</div>
            </div>
            {membership?.role && (
              <span className="ml-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-brand-300">
                {membership.role}
              </span>
            )}
          </div>
          <nav className="flex items-center gap-2">
            <a href="#dashboard" className="rounded-lg px-3 py-1.5 text-sm font-medium text-neutral-300 hover:bg-white/5">Dashboard</a>
            <a href="#setup" className="rounded-lg px-3 py-1.5 text-sm font-medium text-neutral-300 hover:bg-white/5">Setup</a>
            <a href="#settings" className="rounded-lg px-3 py-1.5 text-sm font-medium text-neutral-300 hover:bg-white/5">Settings</a>
            <a href="#shop" className="rounded-lg px-3 py-1.5 text-sm font-medium text-neutral-300 hover:bg-white/5">Shop</a>
            <QuoteCart />
            {canSeeBrain && (
              <button
                type="button"
                onClick={() => setBrainOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-brand-500/30 bg-brand-500/10 px-3 py-1.5 text-sm font-medium text-brand-200 transition hover:border-brand-400 hover:bg-brand-500/20"
                title="Ask the Brain"
              >
                <Sparkles className="h-4 w-4" />
                Ask Brain
              </button>
            )}
            <button onClick={() => signOut()} className="btn-secondary ml-2">Sign out</button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 py-6">
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              G'day{firstName ? `, ${firstName}` : ''}.
            </h1>
            <p className="mt-0.5 text-xs text-neutral-500">
              Inspection dashboard · {activeTenant?.name ?? tenant.tenant.name}
            </p>
          </div>
        </div>

        <InspectionDashboard />
      </main>

      {/* Brain drawer — LEFT side on desktop (md+), bottom sheet on mobile */}
      {canSeeBrain && brainOpen && (
        <>
          <button
            type="button"
            aria-label="Close Brain"
            className="fixed inset-0 z-40 bg-neutral-950/60 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none"
            onClick={() => setBrainOpen(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 h-[85vh] rounded-t-2xl border border-white/10 shadow-2xl shadow-black/60 md:inset-y-0 md:left-0 md:right-auto md:h-auto md:w-[460px] md:rounded-none md:border-l-0 md:border-r">
            <BrainPanel onClose={() => setBrainOpen(false)} />
          </div>
        </>
      )}
    </div>
  );
}
